import type { CommandKind, DeckorateCommand, RankedCommand, ShapeTypeHint } from "../../../types";

export interface CategoryAccent {
  readonly bg: string;
  readonly fg: string;
}

/** Tints each row's icon chip by category for faster scanning — shared by
 * CommandPalette.tsx (in-pane overlay) and AllActionsDialogApp.tsx (Office
 * Dialog), which render the same catalog in two separate bundles. */
const CATEGORY_ACCENT: Record<string, CategoryAccent> = {
  Navigate: { bg: "rgba(11, 93, 170, 0.12)", fg: "#0B5DAA" },
  Design: { bg: "rgba(255, 86, 0, 0.12)", fg: "#FF5600" },
  Content: { bg: "rgba(26, 158, 143, 0.12)", fg: "#1A9E8F" },
  Assets: { bg: "rgba(120, 69, 144, 0.12)", fg: "#784590" },
  Brand: { bg: "rgba(245, 166, 35, 0.16)", fg: "#D88A0E" },
  Open: { bg: "rgba(11, 93, 170, 0.12)", fg: "#0B5DAA" },
  Color: { bg: "rgba(166, 61, 125, 0.12)", fg: "#A63D7D" },
  Map: { bg: "rgba(30, 111, 139, 0.14)", fg: "#1E6F8B" },
};
const FORMAT_ACCENT: CategoryAccent = { bg: "rgba(107, 114, 128, 0.14)", fg: "#6B7280" };
const AI_ACCENT: CategoryAccent = { bg: "rgba(126, 87, 194, 0.14)", fg: "#7E57C2" };

export function getCategoryAccent(category: string): CategoryAccent {
  if (category.startsWith("Format")) return FORMAT_ACCENT;
  if (category.startsWith("AI")) return AI_ACCENT;
  return CATEGORY_ACCENT[category] ?? FORMAT_ACCENT;
}

/** Hoisted to avoid Sonar's duplicated-string-literal smell — `kind: PENDING`
 * appears once per catalog entry that has no live UI to drive yet. */
const PENDING: CommandKind = "pending";

// Apply Brand quick actions: the only catalog rows that drive a
// domActionSequence (scope, then confirm) rather than a single click.
//
// The Design/Content/Assets/"Go to …" placeholder rows that used to sit here
// were removed: none of them had a UI to drive, so every one
// answered "isn't wired up yet", and none appear in the approved mockup's
// action list. Their tab/domAction wiring lives in git history if those areas
// ship later.
const BRAND_ACTION_COMMANDS: readonly DeckorateCommand[] = [
  {
    id: "brand-apply-selected", label: "Apply Brand to Selected Slide", category: "Brand", icon: "🎨",
    kind: "dom-click", tab: "apply-brand",
    domActionSequence: ["brand-apply-scope-slide", "brand-apply-confirm"],
  },
  {
    id: "brand-apply-all", label: "Apply Brand to All Slides", category: "Brand", icon: "🎨",
    kind: "dom-click", tab: "apply-brand",
    domActionSequence: ["brand-apply-scope-deck", "brand-apply-confirm"],
  },
];

// Ported from the DecKorate POC catalog — these categories have no AIVA
// feature yet, kept "pending" so the palette stays searchable (AIVA-1399).

const OPEN_PANEL_COMMANDS: readonly DeckorateCommand[] = [
  { id: "open-create-slides", label: "Create Slides", category: "Open", icon: "✨",
    kind: "navigate", tab: "create-slide", code: "CS",
    keywords: ["new", "wizard", "ai create", "generate"] },
  { id: "open-apply-brand", label: "Apply Brand", category: "Open", icon: "🎯",
    kind: "navigate", tab: "apply-brand", code: "BR",
    keywords: ["brand", "apply", "colors", "fonts", "template"] },
  { id: "open-brand-check", label: "Brand Check", category: "Open", icon: "✅",
    kind: "navigate", tab: "brand-check", code: "BC",
    keywords: ["check", "audit", "compliance", "scan"] },
  // Id kept from when this row was labelled "Design Hub" so existing
  // recents/favorites still point at it. Navigating to "asset-library" opens
  // the library dialog rather than switching panels (DeckorateModule).
  //
  // No `code` here despite the mnemonic temptation ("DH" for "Design Hub"):
  // Format Tools' Distribute Horizontally owns "DH" per the shipped code
  // reference, and Asset Library's own Code Mode letter ("L") is already
  // wired separately via codeMode/table.ts's ASSET_LIBRARY_ENTRY — this
  // catalog code would only have fed AllActionsDialogApp's CODE_INDEX, where
  // colliding with Distribute Horizontally silently drops one of the two.
  { id: "open-design-hub", label: "Asset Library", category: "Open", icon: "📚",
    kind: "navigate", tab: "asset-library",
    keywords: ["templates", "library", "layouts", "design hub", "assets"] },
  { id: "open-format-tools", label: "Format Tools", category: "Open", icon: "🛠️",
    kind: "navigate", tab: "format", code: "FT",
    keywords: ["format", "align", "distribute", "arrange"] },
  { id: "open-color-tools", label: "Color Tools", category: "Open", icon: "🎨",
    kind: "navigate", tab: "color", code: "CT",
    keywords: ["color", "fill", "line", "shuffle"] },
  { id: "open-org-chart", label: "Org Chart", category: "Open", icon: "🧑‍🤝‍🧑",
    kind: "navigate", tab: "org-chart", code: "OC",
    keywords: ["organization", "hierarchy", "people"] },
  { id: "open-map-locations", label: "Plot Locations on Map", category: "Open", icon: "🗺️",
    kind: "navigate", tab: "map", code: "MAP",
    keywords: ["map", "geography", "location", "marker"] },
];

const COLOR_TOOL_COMMANDS: readonly DeckorateCommand[] = [
  { id: "color-no-fill", label: "No Fill", category: "Color", icon: "⬜",
    kind: "dom-click", tab: "color", domAction: "color-no-fill", code: "NF",
    requiresSelection: true, keywords: ["fill", "transparent", "remove fill"] },
  { id: "color-no-line", label: "No Line", category: "Color", icon: "▢",
    kind: "dom-click", tab: "color", domAction: "color-no-line", code: "NL",
    requiresSelection: true, keywords: ["line", "border", "outline", "remove"] },
  { id: "color-shuffle", label: "Shuffle Colors", category: "Color", icon: "🎲",
    kind: "dom-click", tab: "color", domAction: "color-shuffle", code: "SC",
    requiresSelection: true, keywords: ["shuffle", "random", "rotate"] },
  { id: "color-replace", label: "Replace Color", category: "Color", icon: "🔁",
    kind: "dom-click", tab: "color", domAction: "color-replace",
    keywords: ["replace", "find", "swap color"] },
  { id: "color-on-brand", label: "Apply On-Brand Fill", category: "Color", icon: "🎨",
    kind: "dom-click", tab: "color", domAction: "color-on-brand",
    requiresSelection: true, keywords: ["on brand", "snap", "brand colors"] },
  { id: "color-show-non-brand", label: "Show Non-Brand Colors", category: "Color", icon: "🔍",
    kind: "dom-click", tab: "color", domAction: "color-show-non-brand",
    keywords: ["non brand", "highlight", "audit"] },
];

type FormatRow = readonly [
  actionName: string,
  label: string,
  subcat: string,
  keywords: readonly string[],
  hint?: ShapeTypeHint,
  code?: string,
];

/** FormatTools.tsx has a real button (`data-cmd-action="format-<actionName>"`)
 * for every group except "Tables & Text" (see formatRowsPending) — so these
 * rows go live as `dom-click`. */
function formatRows(rows: readonly FormatRow[]): DeckorateCommand[] {
  return rows.map(([actionName, label, subcat, keywords, hint, code]) => ({
    id: `format-${actionName}`,
    label,
    category: `Format · ${subcat}`,
    keywords,
    icon: "🔧",
    kind: "dom-click",
    tab: "format",
    domAction: `format-${actionName}`,
    requiresSelection: true,
    shapeTypeHint: hint ?? "any",
    ...(code ? { code } : {}),
  }));
}

/** Tables & Text subcategory: no equivalent buttons exist in FormatTools.tsx
 * yet, so these stay pending (no tab/domAction) until that UI ships. */
function formatRowsPending(rows: readonly FormatRow[]): DeckorateCommand[] {
  return rows.map(([actionName, label, subcat, keywords, hint]) => ({
    id: `format-${actionName}`,
    label,
    category: `Format · ${subcat}`,
    keywords,
    icon: "🔧",
    kind: PENDING,
    requiresSelection: true,
    shapeTypeHint: hint ?? "any",
  }));
}

const FORMAT_TOOL_COMMANDS: readonly DeckorateCommand[] = [
  // Layout & Fit
  ...formatRows([
    ["fitToSlide", "Fit to Slide", "Layout", ["fit", "slide", "size"]],
    ["fitToSlideStretch", "Fit to Slide (Stretch)", "Layout", ["fit", "stretch"], "any", "FTS"],
    ["smartAlign", "Smart Align (Grid)", "Layout", ["smart", "grid", "auto"], "any", "AG"],
    ["multiplyObject", "Multiply into Grid", "Layout", ["multiply", "duplicate", "grid"], "any", "DG"],
    ["sliceObject", "Slice into Pieces", "Layout", ["slice", "split shape"], "any", "SG"],
    ["centralize", "Centralize on Slide", "Layout", ["center", "centralize"]],
    ["alignCircle", "Arrange in Circle", "Layout", ["circle", "ring"], "any", "AIC"],
  ]),
  // Alignment
  ...formatRows([
    ["alignLeft", "Align Left", "Alignment", ["align left"], "any", "AL"],
    ["alignMiddle", "Align Middle (Horizontal)", "Alignment", ["align middle", "horizontal"], "any", "AM"],
    ["alignRight", "Align Right", "Alignment", ["align right"], "any", "AR"],
    ["alignTop", "Align Top", "Alignment", ["align top"], "any", "AT"],
    ["alignCenter", "Align Center (Vertical)", "Alignment", ["align center", "vertical"], "any", "AC"],
    ["alignBottom", "Align Bottom", "Alignment", ["align bottom"], "any", "AB"],
  ]),
  // Distribution & Spacing
  ...formatRows([
    ["distributeHorizontally", "Distribute Horizontally", "Distribution", ["distribute", "horizontal", "even"], "any", "DH"],
    ["distributeVertically", "Distribute Vertically", "Distribution", ["distribute", "vertical", "even"], "any", "DV"],
    ["increaseHorizSpace", "Increase Horizontal Spacing", "Distribution", ["space", "wider"]],
    ["decreaseHorizSpace", "Decrease Horizontal Spacing", "Distribution", ["space", "tighter"]],
    ["increaseVertSpace", "Increase Vertical Spacing", "Distribution", ["space", "taller"]],
    ["decreaseVertSpace", "Decrease Vertical Spacing", "Distribution", ["space", "shorter"]],
  ]),
  // Sizing
  ...formatRows([
    ["sameWidth", "Match Width", "Sizing", ["same width", "match width"], "any", "MW"],
    ["sameHeight", "Match Height", "Sizing", ["same height", "match height"], "any", "MH"],
    ["sameWidthLeft", "Match Width (Anchored Left)", "Sizing", ["same width left"]],
    ["sameHeightTop", "Match Height (Anchored Top)", "Sizing", ["same height top"]],
    ["sameSize", "Match Size", "Sizing", ["same size", "match"], "any", "MS"],
  ]),
  // Stretching
  ...formatRows([
    ["stretchLeft", "Stretch to Leftmost", "Sizing", ["stretch left"], "any", "SL"],
    ["stretchRight", "Stretch to Rightmost", "Sizing", ["stretch right"], "any", "SR"],
    ["stretchUp", "Stretch to Topmost", "Sizing", ["stretch up", "stretch top"], "any", "ST"],
    ["stretchDown", "Stretch to Bottommost", "Sizing", ["stretch down", "stretch bottom"], "any", "SB"],
    ["pullLeft", "Pull to Leftmost", "Sizing", ["pull left"]],
    ["pullRight", "Pull to Rightmost", "Sizing", ["pull right"]],
    ["edgeMatchTop", "Edge Match Top", "Sizing", ["edge top"]],
    ["edgeMatchBottom", "Edge Match Bottom", "Sizing", ["edge bottom"]],
  ]),
  // Duplication
  ...formatRows([
    ["duplicateLeft", "Duplicate Left", "Duplicate", ["dupe left"], "any", "DL"],
    ["duplicateRight", "Duplicate Right", "Duplicate", ["dupe right"], "any", "DR"],
    ["duplicateUp", "Duplicate Up", "Duplicate", ["dupe up"], "any", "DU"],
    ["duplicateDown", "Duplicate Down", "Duplicate", ["dupe down"], "any", "DD"],
    ["stackHorizontal", "Stack Horizontally", "Duplicate", ["stack", "horizontal"], "any", "SH"],
    ["stackVertical", "Stack Vertically", "Duplicate", ["stack", "vertical"], "any", "SV"],
  ]),
  // Docking
  ...formatRows([
    ["dockLeft", "Dock Left", "Dock", ["dock left", "snap left"]],
    ["dockRight", "Dock Right", "Dock", ["dock right"]],
    ["dockTop", "Dock Top", "Dock", ["dock top"]],
    ["dockBottom", "Dock Bottom", "Dock", ["dock bottom"]],
    ["dockTopLeft", "Dock Top-Left", "Dock", ["dock corner"]],
    ["dockBottomRight", "Dock Bottom-Right", "Dock", ["dock corner"]],
  ]),
  // Arrange
  ...formatRows([
    ["arrangeTopLeft", "Arrange Top-Left", "Arrange", ["arrange corner"]],
    ["arrangeTopRight", "Arrange Top-Right", "Arrange", ["arrange corner"]],
    ["arrangeBottomLeft", "Arrange Bottom-Left", "Arrange", ["arrange corner"]],
    ["arrangeBottomRight", "Arrange Bottom-Right", "Arrange", ["arrange corner"]],
  ]),
  // Swap
  ...formatRows([
    ["swapHorizontally", "Swap Horizontally", "Swap", ["swap horizontal"]],
    ["swapVertically", "Swap Vertically", "Swap", ["swap vertical"]],
    ["swapObjects", "Swap Objects", "Swap", ["swap shapes"], "any", "SW"],
    ["swapText", "Swap Text", "Swap", ["swap text"]],
    ["swapColor", "Swap Colors", "Swap", ["swap color"]],
  ]),
  // Text margins — the Margins tab (ported from VSTO's MARGIN section).
  ...formatRows([
    ["setMargin", "Set Margin\u2026", "Margins", ["margin", "inset", "padding", "text frame"]],
    ["marginAll", "0-Margin All", "Margins", ["margin", "zero", "all"]],
    ["marginLeft", "0-Margin Left", "Margins", ["margin", "zero", "left"]],
    ["marginRight", "0-Margin Right", "Margins", ["margin", "zero", "right"]],
    ["marginTop", "0-Margin Top", "Margins", ["margin", "zero", "top"]],
    ["marginBottom", "0-Margin Bottom", "Margins", ["margin", "zero", "bottom"]],
    ["marginLeftRight", "0-Margin Left & Right", "Margins", ["margin", "zero", "horizontal"]],
    ["marginTopBottom", "0-Margin Top & Bottom", "Margins", ["margin", "zero", "vertical"]],
    ["marginTopLeft", "0-Margin Top & Left", "Margins", ["margin", "zero", "corner"]],
    ["marginTopRight", "0-Margin Top & Right", "Margins", ["margin", "zero", "corner"]],
    ["marginBottomLeft", "0-Margin Bottom & Left", "Margins", ["margin", "zero", "corner"]],
    ["marginBottomRight", "0-Margin Bottom & Right", "Margins", ["margin", "zero", "corner"]],
  ]),
  // Copy Style + the Apply … family (ported from VSTO's PickApplyFormatting).
  ...formatRows([
    ["copyStyle", "Copy Style", "Copy Style", ["copy", "pick", "style", "format painter"]],
    ["applyWidth", "Apply Width", "Copy Style", ["paste", "width"]],
    ["applyHeight", "Apply Height", "Copy Style", ["paste", "height"]],
    ["applySize", "Apply Size", "Copy Style", ["paste", "size"]],
    ["applyPosition", "Apply Position", "Copy Style", ["paste", "position"]],
    ["applyShapeStyle", "Apply Shape Style", "Copy Style", ["paste", "shape style", "fill", "outline"]],
    ["applyTextFormat", "Apply Text Format", "Copy Style", ["paste", "font", "text format"]],
    ["applyColor", "Apply Color", "Copy Style", ["paste", "fill", "color"]],
    ["applyOutline", "Apply Outline", "Copy Style", ["paste", "outline", "border"]],
    ["applyProportions", "Apply Proportions", "Copy Style", ["paste", "adjustments", "proportions"]],
    ["applyAllFormatting", "Apply All Formatting", "Copy Style", ["paste", "all formatting"]],
  ]),
  // Wrap Text + Single Line Spacing live in the Tables & Text tab but, unlike
  // the pending rows below, they have working buttons wired to real ops.
  ...formatRows([
    ["wrapText", "Wrap Text", "Text", ["wrap", "word wrap", "no wrap"]],
    ["singleLineSpacing", "Single Line Spacing", "Text", ["line spacing", "single", "leading", "space before"]],
  ]),
  // Tables & Text — no real buttons in FormatTools.tsx yet; stays pending.
  ...formatRowsPending([
    ["tableToTextExplode", "Table to Text Boxes (Explode)", "Table", ["table", "explode"], "table"],
    ["tableToTextAsIs", "Table to Text Boxes (As-Is)", "Table", ["table", "ungroup"], "table"],
    ["splitTable", "Split Table", "Table", ["split table"], "table"],
    ["alignShapesAsTable", "Align Shapes as Table", "Table", ["table", "grid"]],
    ["deleteAllText", "Delete All Text", "Text", ["delete text"]],
    ["splitText", "Split Text into Shapes", "Text", ["split text", "explode"]],
    ["mergeText", "Merge Text", "Text", ["merge", "combine"]],
    ["sortAscending", "Sort Ascending", "Text", ["sort", "asc"]],
    ["sortDescending", "Sort Descending", "Text", ["sort", "desc"]],
    ["removeLineBreaks", "Remove Line Breaks", "Text", ["line breaks"]],
    ["reorderShapes", "Reorder Shapes", "Layout", ["reorder", "z-order"]],
    ["replaceFont", "Replace Font", "Text", ["font", "replace"]],
    ["connectorWeight", "Set Connector Weight", "Text", ["connector", "line weight"]],
  ]),
];

type AiRow = readonly [
  id: string,
  label: string,
  category: string,
  icon: string,
  keywords: readonly string[],
  shapeTypeHint?: ShapeTypeHint,
];

/** The two AI Designer commands still awaiting a panel open the AI-preview
 * dialog (CommandPalette.tsx) as a placeholder UI; `shapeTypeHint` presence
 * (not a separate flag) marks which ones require a selection. Fix
 * Alignment/Infographic/Speaker Notes graduated out to AI_LIVE_COMMANDS above
 * once they got real panels.
 *
 * The per-selection AI text/image/table rows that used to live here (improve
 * wording, vectorize, remove background, summarize table, …) were removed:
 * they were placeholders the mockup's action list doesn't carry,
 * and every one of them is offered by the chat action chips for the matching
 * selection instead (see features/action-chips/chipConfig.ts). */
function aiRows(rows: readonly AiRow[]): DeckorateCommand[] {
  return rows.map(([id, label, category, icon, keywords, shapeTypeHint]) => ({
    id, label, category, icon, keywords,
    kind: "ai-preview",
    ...(shapeTypeHint ? { requiresSelection: true, shapeTypeHint } : {}),
  }));
}

// These AI Designer rows now have a real panel, so they're "navigate"
// instead of aiRows()'s "ai-preview" stub — same id as before so existing
// recents/favorites keep pointing at the same row.
const AI_LIVE_COMMANDS: readonly DeckorateCommand[] = [
  { id: "ai-redesign-slide", label: "Transform Slide (AI)", category: "AI · Slide", icon: "🪄",
    kind: "navigate", tab: "slide-transform",
    keywords: ["transform", "redesign", "restyle", "rework", "make it look better", "ai design",
      // Both modes live behind this one row (AI design / match a template), so
      // someone searching for the template path has to land here too.
      "template", "templates", "layout", "match a layout"] },
  { id: "ai-fix-alignment", label: "Fix Alignment (AI)", category: "AI · Slide", icon: "📐",
    kind: "navigate", tab: "fix-alignment",
    keywords: ["fix", "align", "auto align", "straighten"] },
  { id: "ai-convert-infographic", label: "Convert to Infographic (AI)", category: "AI · Convert", icon: "📊",
    kind: "navigate", tab: "infographic",
    keywords: ["infographic", "visualize"] },
  { id: "ai-speaker-notes", label: "Add Speaker Notes (AI)", category: "AI · Slide", icon: "📝",
    kind: "navigate", tab: "speaker-notes",
    keywords: ["speaker notes", "presenter notes", "talking points"] },
  { id: "ai-insights", label: "Get Insights from Slide (AI)", category: "AI · Analysis", icon: "💡",
    kind: "navigate", tab: "insights",
    keywords: ["insights", "analyze", "findings"] },
  { id: "ai-photo-head", label: "Create Photo Headshot (AI)", category: "AI · Image", icon: "👤",
    kind: "navigate", tab: "photo-headshot", requiresSelection: true, shapeTypeHint: "image",
    keywords: ["headshot", "photo head", "portrait"] },
  { id: "ai-sketch-to-slide", label: "Convert Sketch to Slide (AI)", category: "AI · Image", icon: "✏️",
    kind: "navigate", tab: "sketch-to-slide", requiresSelection: true, shapeTypeHint: "image",
    keywords: ["sketch", "wireframe", "hand-drawn", "sketch to slide"] },
  { id: "ai-image-to-chart", label: "Convert Image to Chart (AI)", category: "AI · Convert", icon: "📈",
    kind: "open-sheet", sheet: "convert-chart", requiresSelection: true, shapeTypeHint: "image",
    keywords: ["chart", "image to chart", "convert chart", "graph", "editable chart", "picture to chart"] },
];

const AI_DESIGNER_COMMANDS: readonly DeckorateCommand[] = aiRows([
  ["ai-table-to-chart", "Convert Table to Chart (AI)", "AI · Convert", "📈",
    ["chart", "visualize", "table to chart"], "table"],
]);

// Map Plot (AIVA-5434) — has a real panel with data-cmd-action-wired buttons,
// unlike the ported-from-POC categories above, so these go live from the start.
const MAP_PLOT_COMMANDS: readonly DeckorateCommand[] = [
  { id: "map-insert-slide", label: "Insert Map Slide", category: "Map", icon: "🗺️",
    kind: "dom-click", tab: "map", domAction: "map-insert-slide",
    keywords: ["map", "insert", "region"] },
  { id: "map-resolve-regions", label: "Resolve Map Region(s)", category: "Map", icon: "🗺️",
    kind: "dom-click", tab: "map", domAction: "map-resolve-regions",
    keywords: ["map", "shade", "region", "resolve"] },
  { id: "map-apply-region-fill", label: "Apply Map Region Fill", category: "Map", icon: "🎨",
    kind: "dom-click", tab: "map", domAction: "map-apply-region-fill",
    keywords: ["map", "shade", "region", "fill", "color"] },
  { id: "map-resolve-locations", label: "Resolve Map Locations", category: "Map", icon: "📍",
    kind: "dom-click", tab: "map", domAction: "map-resolve-locations",
    keywords: ["map", "location", "geocode", "resolve"] },
  { id: "map-add-markers", label: "Add Map Markers", category: "Map", icon: "📍",
    kind: "dom-click", tab: "map", domAction: "map-add-markers",
    keywords: ["map", "marker", "plot", "pin"] },
];

// Two hero rows with no home above: Compare Versions has a real panel
// (CompareSlides.tsx) so it's "navigate"; Add Icon stays "pending" (AIVA-1399).
const MISC_HERO_COMMANDS: readonly DeckorateCommand[] = [
  { id: "open-compare-versions", label: "Compare Versions", category: "Open", icon: "🔀",
    kind: "navigate", tab: "compare", code: "CV",
    keywords: ["compare", "versions", "diff", "revert", "history"] },
  { id: "textbox-add-icon", label: "Add Icon", category: "Text box", icon: "⭐",
    kind: PENDING, requiresSelection: true, shapeTypeHint: "textbox",
    keywords: ["icon", "insert icon", "suggest icon"] },
];

/** Applied after assembly (aiRows' tuple has no code slot) — a single place
 * to add an "All actions" shortcut code to a command defined elsewhere. */
const CODE_OVERRIDES: Readonly<Record<string, string>> = {
  "ai-fix-alignment": "FA",
};

export const ACTION_CATALOG: readonly DeckorateCommand[] = [
  ...BRAND_ACTION_COMMANDS,
  ...OPEN_PANEL_COMMANDS,
  ...COLOR_TOOL_COMMANDS,
  ...FORMAT_TOOL_COMMANDS,
  ...MAP_PLOT_COMMANDS,
  ...AI_LIVE_COMMANDS,
  ...AI_DESIGNER_COMMANDS,
  ...MISC_HERO_COMMANDS,
].map((cmd) => (CODE_OVERRIDES[cmd.id] ? { ...cmd, code: CODE_OVERRIDES[cmd.id] } : cmd));

/** Coded commands split by `requiresSelection`, shown as footer reference
 * lines in both CommandPalette.tsx and AllActionsDialogApp.tsx. */
const CODED_COMMANDS = ACTION_CATALOG.filter((cmd) => cmd.code);
export const SHAPE_CODES: readonly string[] = CODED_COMMANDS.filter((cmd) => cmd.requiresSelection).map(
  (cmd) => cmd.code as string,
);
export const ANYTIME_CODES: readonly string[] = CODED_COMMANDS.filter((cmd) => !cmd.requiresSelection).map(
  (cmd) => cmd.code as string,
);

function tokenize(query: string): string[] {
  return query.toLowerCase().split(/[\s/_-]+/).filter(Boolean);
}

function scoreToken(token: string, haystack: string): number {
  if (haystack === token) return 100;
  if (haystack.startsWith(token)) return 70;
  if (haystack.includes(token)) return 40;
  return 0;
}

/** Sums each token's match score against `haystack`, or returns `null` the
 * moment any token fails to match at all — the command is dropped. */
function sumTokenScores(tokens: readonly string[], haystack: string): number | null {
  let total = 0;
  for (const token of tokens) {
    const tokenScore = scoreToken(token, haystack);
    if (tokenScore === 0) return null;
    total += tokenScore;
  }
  return total;
}

function labelMatchBonus(labelLower: string, trimmedLower: string): number {
  if (labelLower === trimmedLower) return 500;
  if (labelLower.startsWith(trimmedLower)) return 200;
  return 0;
}

/** POC parity: recents count for less once there's a query (the label match
 * already dominates), and count for more as a browsing-order bonus when the
 * query is empty. */
function recencyBonus(id: string, hasQuery: boolean, recentRank: ReadonlyMap<string, number>): number {
  const recent = recentRank.get(id);
  if (recent === undefined) return 0;
  return recent * (hasQuery ? 10 : 50);
}

function favoriteBonus(id: string, favRank: ReadonlyMap<string, number>): { favorite: boolean; bonus: number } {
  const rank = favRank.get(id);
  return rank === undefined ? { favorite: false, bonus: 0 } : { favorite: true, bonus: 400 + rank };
}

/**
 * Simplified stand-in for the POC's fuzzy filterAndRank: every query token
 * must match the label/category/keywords or the command is dropped, then
 * exact/prefix label matches, recency, and favorites score as bonuses.
 * `catalog` defaults to Deckorate's own; the pane's merged "All actions"
 * palette (ST-05) passes its AiVA + Deckorate list instead.
 */
export function filterAndRank(
  query: string,
  recentIds: readonly string[],
  favoriteIds: readonly string[] = [],
  catalog: readonly DeckorateCommand[] = ACTION_CATALOG,
): RankedCommand[] {
  const trimmedLower = query.trim().toLowerCase();
  const tokens = tokenize(query);
  const hasQuery = tokens.length > 0;
  const recentRank = new Map(recentIds.map((id, i) => [id, recentIds.length - i]));
  const favRank = new Map(favoriteIds.map((id, i) => [id, favoriteIds.length - i]));

  const scored: RankedCommand[] = [];
  for (const cmd of catalog) {
    const haystack = [cmd.label, cmd.category, ...(cmd.keywords ?? [])].join(" ").toLowerCase();
    const tokenScore = sumTokenScores(tokens, haystack);
    if (tokenScore === null) continue;

    const { favorite, bonus: favBonus } = favoriteBonus(cmd.id, favRank);
    const score =
      tokenScore +
      (hasQuery ? labelMatchBonus(cmd.label.toLowerCase(), trimmedLower) : 0) +
      recencyBonus(cmd.id, hasQuery, recentRank) +
      favBonus;

    scored.push({ ...cmd, score, favorite });
  }

  return scored.sort((a, b) => b.score - a.score);
}
